import { useEffect } from 'react'
import { AppStore } from 'app/state/types'
import { BackendEvent } from 'features/common/api/backendEvents'
import { subscribeBackendEvents } from 'features/common/hooks/useBackendEvents'
import { getMapComments, getMapComment } from 'features/maps/api/mapComments'

import { addComment, deleteComment, setComments, updateComment } from '../state/mapCommentsSlice'
import { useAppSelector, useAppStore } from 'app/state/hooks'
import { CommentResponseActionType } from 'features/collaboration/types'
import { queryClient } from 'queryClient'
import { mapsQueryKeys } from '../../maps/api/mapQueries'

const mapCommentBackendEvents = ['MapCommentAdded', 'MapCommentUpdated', 'MapCommentDeleted']

export async function syncMapCommentsProcess(
  mapId: string,
  { store, signal }: { store: AppStore; signal: AbortSignal }
) {
  const handleMapCommentBackentEvent = (event: BackendEvent) => {
    const { id: eventMapId } = event.payload
    if (eventMapId !== mapId) {
      return
    }
    switch (event.type) {
      case 'MapCommentAdded':
      case 'MapCommentUpdated':
        {
          const { commentId } = event.payload
          if (!commentId) return

          syncCommentFromBackend(mapId, commentId, { store, signal })
          queryClient.invalidateQueries({ queryKey: mapsQueryKeys.mapComments(mapId) })
        }
        break
      case 'MapCommentDeleted':
        if (!event.payload.commentId) return

        store.dispatch(deleteComment(event.payload.commentId))
        queryClient.invalidateQueries({ queryKey: mapsQueryKeys.mapComments() })
        break
    }
  }

  let initialSyncDone = false
  const initialSyncEventsBuffer: BackendEvent[] = []

  const unsubscribe = subscribeBackendEvents((event) => {
    if (!mapCommentBackendEvents.includes(event.type)) return

    if (!initialSyncDone) {
      initialSyncEventsBuffer.push(event)
      return
    }
    handleMapCommentBackentEvent(event)
  })

  try {
    // initial sync
    {
      const initialComments = await getMapComments(mapId)
      store.dispatch(setComments(initialComments))
      initialSyncDone = true
    }

    // handle events received while waiting for initial sync
    initialSyncEventsBuffer.forEach(handleMapCommentBackentEvent)

    // just wait for abort, usually - never
    await new Promise((resolve) => {
      signal.addEventListener('abort', resolve)
    })
  } finally {
    unsubscribe()
  }
}

export async function syncCommentFromBackend(
  mapId: string,
  commentId: string,
  { store, signal }: { store: AppStore; signal?: AbortSignal }
) {
  try {
    const comment = await getMapComment(mapId, commentId, { signal })

    const currentCachedInstanceOfComment = store.getState().mapComments.comments.find((c) => c.id === commentId)
    if (currentCachedInstanceOfComment?.updatedAt && currentCachedInstanceOfComment.updatedAt > comment.updatedAt) {
      return currentCachedInstanceOfComment
    }

    if (currentCachedInstanceOfComment) {
      store.dispatch(updateComment({ ...currentCachedInstanceOfComment, ...comment }))
    } else {
      store.dispatch(addComment(comment))
    }
    return comment
  } catch (error) {
    if (isAbortError(error)) return

    console.error(`Failed to load comment: ${error}`, error)
  }
}

export function useMapCommentWithResponses(commentId?: string) {
  const store = useAppStore()
  const mapId = useAppSelector((state) => state.maps.currentMap?.id)
  const comment = store.getState().mapComments.comments.find((c) => c.id === commentId)

  const loadedResposesCount = comment?.responses?.filter(
    (r) => r.actionType === CommentResponseActionType.Reply
  )?.length
  const loaded = comment?.responsesCount ? loadedResposesCount === comment?.responsesCount : true

  useEffect(() => {
    if (!mapId || !commentId) return

    const controller = new AbortController()

    if (loaded) return

    syncCommentFromBackend(mapId, commentId, { store, signal: controller.signal })
    return () => controller.abort()
  }, [mapId, store, commentId, loaded])

  return { loaded, comment }
}

function isAbortError(error: unknown) {
  return (
    error === 'AbortError' || ((error instanceof Error || error instanceof DOMException) && error.name === 'AbortError')
  )
}
